/**
 * Der Prüfassistent — ein Gespräch über die Unterlagen.
 *
 * Zwei Erscheinungsformen desselben Bausteins:
 *
 * - `AssistentGespraech`: der eigentliche Chat (Verlauf, Eingabe, Vorschläge).
 *   Er lebt in der Dokumentlade als „Frage zum Dokument" und im schwebenden
 *   Fenster unten rechts als Assistent über die ganze Prüfung.
 * - `AssistentKnopf` + `AssistentFenster`: der runde Knopf unten rechts und
 *   das Fenster, das er öffnet. Das Fenster bleibt über jeder Ansicht, der
 *   Verlauf überlebt das Schließen — bis „Neues Gespräch".
 *
 * Der Verlauf lebt nur im Browser. Jede Frage schickt ihn mit, der Server
 * speichert nichts. Welche Unterlage oder welcher Beleg gerade offen ist,
 * geht als Kontext mit — dann kennt der Assistent deren vollen Text.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { api } from '../lib/api';

export interface Nachricht {
  rolle: 'nutzer' | 'assistent';
  text: string;
  fehler?: boolean;
}

export interface Bezug {
  dokumentId?: string | null;
  belegId?: string | null;
}

export function AssistentGespraech({
  pruefungId,
  bezug,
  verlauf,
  onVerlauf,
  vorschlaege,
  platzhalter,
  kompakt,
  verfuegbar,
  hinweisWennAus,
  autoFokus,
}: {
  pruefungId: string;
  bezug?: Bezug;
  verlauf: Nachricht[];
  onVerlauf: (v: Nachricht[]) => void;
  vorschlaege?: string[];
  platzhalter?: string;
  /** Schmale Fassung für die Dokumentlade. */
  kompakt?: boolean;
  verfuegbar: boolean | null;
  hinweisWennAus?: ReactNode;
  autoFokus?: boolean;
}) {
  const [eingabe, setEingabe] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const liste = useRef<HTMLDivElement>(null);
  const feld = useRef<HTMLTextAreaElement>(null);

  // Nur die Liste selbst scrollen — scrollIntoView würde auch die Lade
  // oder das Fenster verschieben und den Kopf aus dem Bild drücken.
  useEffect(() => {
    const el = liste.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [verlauf, laeuft]);

  useEffect(() => {
    if (autoFokus) feld.current?.focus({ preventScroll: true });
  }, [autoFokus]);

  async function fragen(text: string) {
    const frage = text.trim();
    if (!frage || laeuft) return;
    const neu: Nachricht[] = [...verlauf, { rolle: 'nutzer', text: frage }];
    onVerlauf(neu);
    setEingabe('');
    setLaeuft(true);
    try {
      const a = await api.assistent.fragen(pruefungId, {
        frage,
        verlauf: verlauf.filter((n) => !n.fehler).map((n) => ({ rolle: n.rolle, text: n.text })),
        dokumentId: bezug?.dokumentId ?? null,
        belegId: bezug?.belegId ?? null,
      });
      onVerlauf([...neu, { rolle: 'assistent', text: a.antwort }]);
    } catch (g) {
      onVerlauf([...neu, { rolle: 'assistent', text: (g as Error).message, fehler: true }]);
    } finally {
      setLaeuft(false);
      feld.current?.focus({ preventScroll: true });
    }
  }

  if (verfuegbar === false) {
    return (
      <div className={`assistent-aus${kompakt ? ' kompakt' : ''}`}>
        {hinweisWennAus ?? 'Der Assistent ist für diese Installation nicht eingerichtet.'}
      </div>
    );
  }

  return (
    <div className={`assistent-gespraech${kompakt ? ' kompakt' : ''}`}>
      <div className="assistent-verlauf" aria-live="polite" ref={liste}>
        {verlauf.length === 0 && vorschlaege && vorschlaege.length > 0 && (
          <div className="assistent-vorschlaege">
            {vorschlaege.map((v) => (
              <button key={v} type="button" className="assistent-vorschlag" onClick={() => fragen(v)} disabled={laeuft}>
                {v}
              </button>
            ))}
          </div>
        )}
        {verlauf.map((n, i) => (
          <div key={i} className={`assistent-blase ${n.rolle}${n.fehler ? ' fehler' : ''}`}>
            {n.text}
          </div>
        ))}
        {laeuft && (
          <div className="assistent-blase assistent wartet" aria-label="Der Assistent antwortet">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>
      <form
        className="assistent-eingabe"
        onSubmit={(e) => {
          e.preventDefault();
          void fragen(eingabe);
        }}
      >
        <textarea
          ref={feld}
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void fragen(eingabe);
            }
          }}
          rows={kompakt ? 1 : 2}
          placeholder={platzhalter ?? 'Frage stellen …'}
          disabled={laeuft || verfuegbar === null}
          aria-label="Frage an den Assistenten"
        />
        <button className="btn primary sm" type="submit" disabled={laeuft || !eingabe.trim() || verfuegbar === null}>
          Senden
        </button>
      </form>
      {!kompakt && <p className="assistent-fuss">Der Assistent ist eine KI und kann sich irren. Prüfen Sie Zahlen an der Quelle — die Beurteilung bleibt bei Ihnen.</p>}
    </div>
  );
}

/** Der runde Knopf unten rechts. */
export function AssistentKnopf({ offen, onClick, ungelesen }: { offen: boolean; onClick: () => void; ungelesen?: boolean }) {
  return (
    <button className={`assistent-knopf${offen ? ' offen' : ''}`} onClick={onClick} aria-expanded={offen} aria-label="Prüfassistent öffnen" title="Prüfassistent">
      <AssistentSymbol />
      <span className="assistent-knopf-text">Assistent</span>
      {ungelesen && <span className="assistent-punkt" aria-hidden="true" />}
    </button>
  );
}

/** Ein eigenes Zeichen: eine Sprechblase mit einem Prüfhaken. */
export function AssistentSymbol({ gross }: { gross?: boolean }) {
  const n = gross ? 28 : 20;
  return (
    <svg width={n} height={n} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.6L3 21l1.9-5.6A8.5 8.5 0 1 1 21 11.5z" />
      <path d="M8.5 11.7l2.3 2.3 4.7-4.7" />
    </svg>
  );
}

export function AssistentFenster({
  pruefungId,
  bezug,
  bezugText,
  verlauf,
  onVerlauf,
  verfuegbar,
  istBeirat,
  onSchliessen,
}: {
  pruefungId: string;
  bezug?: Bezug;
  /** Was gerade offen ist, in Worten — damit man sieht, worauf sich der Assistent bezieht. */
  bezugText?: string | null;
  verlauf: Nachricht[];
  onVerlauf: (v: Nachricht[]) => void;
  verfuegbar: boolean | null;
  istBeirat: boolean;
  onSchliessen: () => void;
}) {
  const [gross, setGross] = useState(false);
  return (
    <div className={`assistent-fenster${gross ? ' gross' : ''}`} role="dialog" aria-label="Prüfassistent">
      <div className="assistent-kopf">
        <span className="assistent-kopf-symbol">
          <AssistentSymbol />
        </span>
        <div style={{ minWidth: 0 }}>
          <b>Prüfassistent</b>
          <small>{bezugText ? `Bezug: ${bezugText}` : 'Alle Unterlagen, Belege und Rückfragen dieser Prüfung'}</small>
        </div>
        <div className="spacer" />
        {verlauf.length > 0 && (
          <button className="btn ghost sm" onClick={() => onVerlauf([])}>
            Neues Gespräch
          </button>
        )}
        <button className="iconbtn" onClick={() => setGross((g) => !g)} aria-label={gross ? 'Kleiner' : 'Größer'} title={gross ? 'Kleiner' : 'Größer'}>
          {gross ? '⤡' : '⤢'}
        </button>
        <button className="iconbtn" onClick={onSchliessen} aria-label="Schließen">
          ✕
        </button>
      </div>
      <AssistentGespraech
        pruefungId={pruefungId}
        bezug={bezug}
        verlauf={verlauf}
        onVerlauf={onVerlauf}
        verfuegbar={verfuegbar}
        autoFokus
        platzhalter="Frage zur Prüfung stellen — z. B. „Welche Belege über 1.000 € sind noch offen?“"
        vorschlaege={[
          'Stimmen die Anfangs- und Endbestände der Konten?',
          'Welche Belege über 1.000 € sind noch nicht geprüft?',
          'Was steht im Wirtschaftsplan zur Erhaltungsrücklage?',
          'Fasse die Gesamtabrechnung in fünf Sätzen zusammen.',
          'Welche Rückfragen sind noch offen?',
        ]}
        hinweisWennAus={
          istBeirat
            ? 'Der Assistent ist für diese Prüfung nicht verfügbar.'
            : 'Der Assistent ist noch nicht eingerichtet: In server/.env fehlt ANTHROPIC_API_KEY. Hinweis: Die Unterlagen gehen dann an den KI-Anbieter — vorher den Auftragsverarbeitungsvertrag klären.'
        }
      />
    </div>
  );
}
